import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { tripAPI, busAPI, cityCodeAPI, promoAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { generateVehicleLayout } from '../utils/seatLayout';

const BOOKING_STATUS_LABELS = {
  payment_submitted: { label: 'إيصال مرفوع', cls: 'bg-yellow-100 text-yellow-700' },
  payment_confirmed: { label: 'دفع مؤكد',    cls: 'bg-blue-100 text-blue-700'   },
  ticket_issued:     { label: 'تذكرة صادرة',  cls: 'bg-green-100 text-green-700' },
};

// UTC-based formatters — times are stored as naive UTC strings so we read them back in UTC
const fmtUTC = (iso) => {
  const d = new Date(iso);
  return `${d.getUTCFullYear()}/${String(d.getUTCMonth()+1).padStart(2,'0')}/${String(d.getUTCDate()).padStart(2,'0')} ${String(d.getUTCHours()).padStart(2,'0')}:${String(d.getUTCMinutes()).padStart(2,'0')}`;
};
const fmtUTCDate = (iso) => {
  const d = new Date(iso);
  return `${d.getUTCFullYear()}/${String(d.getUTCMonth()+1).padStart(2,'0')}/${String(d.getUTCDate()).padStart(2,'0')}`;
};
const fmtUTCInput = (iso) => {
  const d = new Date(iso);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}T${String(d.getUTCHours()).padStart(2,'0')}:${String(d.getUTCMinutes()).padStart(2,'0')}`;
};

// type="number" silently commits an empty string when the browser can't parse
// what's in the box — no error, no visual cue, it just doesn't submit. The
// most common way that happens here: Arabic keyboards/IMEs producing
// Arabic-Indic digits (٠-٩) or the Arabic decimal separator (٫) instead of
// ASCII ones, which <input type="number"> rejects outright. The capacity
// fields below use type="text" + these normalizers instead, so what's typed
// always becomes a real ASCII number RHF can read back at submit time.
const ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const toAsciiDigit = (ch) => {
  const i = ARABIC_INDIC_DIGITS.indexOf(ch);
  return i > -1 ? String(i) : ch;
};
const normalizeDecimalInput = (raw) =>
  String(raw ?? '')
    .replace(/[٠-٩]/g, toAsciiDigit)
    .replace(/[٫،]/g, '.')          // Arabic decimal separator / thousands separator → '.'
    .replace(/[^0-9.]/g, '')        // drop anything else (spaces, stray letters, RTL marks)
    .replace(/(\..*)\./g, '$1');    // collapse a second '.' instead of rejecting the keystroke
const normalizeIntInput = (raw) =>
  String(raw ?? '')
    .replace(/[٠-٩]/g, toAsciiDigit)
    .replace(/[^0-9]/g, '');

// codes = { cityName: 'COD', … } fetched from DB at runtime
const cc = (name, codes) =>
  (codes && codes[name]) || (name || '').replace(/[^a-zA-Z]/g, '').substring(0, 3).toUpperCase() || 'XXX';
const compCode = (name) =>
  (name || 'SAF').replace(/[^a-zA-Z]/g, '').substring(0, 3).toUpperCase() || 'SAF';
const buildTicketNumber = (trip, seatNum, codes) => {
  const d      = new Date(trip?.departureTime);
  const dd     = String(d.getUTCDate()).padStart(2, '0');
  const mm     = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yy     = String(d.getUTCFullYear()).slice(2);
  const prefix = trip?.company?.code
    ? trip.company.code.toUpperCase()
    : compCode(trip?.company?.name);
  const rawPlate = trip?.bus?.plateNumber || '';
  const busNum   = rawPlate
    ? rawPlate.replace(/\s+/g, '').toUpperCase().substring(0, 8)
    : (trip?.busId ? `B${trip.busId.substring(0, 6)}` : 'BUS');
  return `${prefix}-${cc(trip?.fromCity, codes)}-${cc(trip?.toCity, codes)}-${dd}${mm}${yy}-${busNum}-${seatNum}`;
};

// ── Passenger manifest modal ──────────────────────────────────────────────────
const PassengerModal = ({ trip, onClose, codes }) => {
  const { user }                        = useAuth();
  const [bookings, setBookings]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [reminding, setReminding]       = useState(false);
  const [printing, setPrinting]         = useState(false);

  const load = () => {
    setLoading(true);
    tripAPI.getPassengers(trip.id)
      .then((res) => setBookings(res.data.data))
      .catch(() => toast.error('فشل في تحميل قائمة المسافرين'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [trip.id]); // eslint-disable-line

  const totalSeats   = bookings.reduce((s, b) => s + (b.seats?.length || 0), 0);
  const totalRevenue = bookings.reduce((s, b) => s + parseFloat(b.totalAmount || 0), 0);
  const boardedCount = bookings.filter((b) => b.boardedAt).reduce((s, b) => s + (b.seats?.length || 1), 0);
  const issuedCount  = bookings.filter((b) => b.status === 'ticket_issued').reduce((s, b) => s + (b.seats?.length || 1), 0);

  // Expand each booking into one row per seat/passenger
  const allRows = bookings.flatMap((b) => {
    const details = b.passengerDetails;
    if (details && details.length > 0) {
      return details.map((pd, pdIdx) => ({
        booking: b,
        seat: pd.seat,
        name: pd.name || b.passenger?.name || '—',
        phone: pd.phone || b.passenger?.phone || '—',
        isFirst: pdIdx === 0,
        perSeatAmount: parseFloat(b.totalAmount) / b.seats.length,
        ticketNumber: b.status === 'ticket_issued' ? buildTicketNumber(trip, pd.seat, codes) : null,
      }));
    }
    // Fallback for bookings without passengerDetails
    return (b.seats || []).slice().sort((a, z) => a - z).map((seat, seatIdx) => ({
      booking: b,
      seat,
      name:  seatIdx === 0 ? (b.passenger?.name  || '—') : '—',
      phone: seatIdx === 0 ? (b.passenger?.phone || '—') : '—',
      isFirst: seatIdx === 0,
      perSeatAmount: parseFloat(b.totalAmount) / b.seats.length,
      ticketNumber: b.status === 'ticket_issued' ? buildTicketNumber(trip, seat, codes) : null,
    }));
  });

  const handleReminder = async () => {
    setReminding(true);
    try {
      const res = await tripAPI.sendDepartureReminder(trip.id);
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.message || 'فشل في إرسال التذكير');
    } finally { setReminding(false); }
  };

  const handlePrintManifest = async () => {
    setPrinting(true);
    try {
      const res = await tripAPI.getManifestPdf(trip.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error('تعذّر إنشاء كشف الركاب');
    } finally { setPrinting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" dir="rtl">
        {/* Header */}
        <div className="p-5 border-b flex items-start justify-between gap-4 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-800">🧑‍🤝‍🧑 قائمة مسافري الرحلة</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {trip.fromCity} → {trip.toCity} · {fmtUTC(trip.departureTime)}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={handlePrintManifest}
              disabled={printing}
              className="flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white px-3.5 py-2 rounded-lg text-xs font-semibold transition"
              title="تحميل كشف الركاب (PDF) — يتضمن بيانات الشركة والسائق ولوحة الباص"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H8a2 2 0 01-2-2V5a2 2 0 012-2h6l6 6v11a2 2 0 01-2 2z" />
              </svg>
              {printing ? 'جاري الإنشاء...' : 'كشف الركاب PDF'}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
          </div>
        </div>

        {/* Summary bar */}
        <div className="px-5 py-3 bg-gray-50 border-b flex-shrink-0">
          <div className="flex gap-5 text-sm flex-wrap">
            <div><span className="text-gray-500">الحجوزات: </span><span className="font-bold text-gray-800">{bookings.length}</span></div>
            <div><span className="text-gray-500">المقاعد: </span><span className="font-bold text-gray-800">{totalSeats}</span></div>
            <div><span className="text-gray-500">الإيرادات: </span><span className="font-bold text-green-700">{totalRevenue.toLocaleString()} ج</span></div>
            {issuedCount > 0 && (
              <>
                <div><span className="text-gray-500">صعدوا: </span><span className="font-bold text-green-600">{boardedCount}</span></div>
                <div><span className="text-gray-500">لم يصلوا: </span><span className="font-bold text-red-600">{issuedCount - boardedCount}</span></div>
              </>
            )}
          </div>

          {/* Departure reminder — company admin only */}
          {user?.role === 'company_admin' && issuedCount > 0 && (issuedCount - boardedCount) > 0 && (
            <button
              onClick={handleReminder}
              disabled={reminding}
              className="mt-3 flex items-center gap-2 bg-orange-50 hover:bg-orange-100 disabled:opacity-60 text-orange-700 border border-orange-200 px-4 py-2 rounded-lg text-xs font-semibold transition"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {reminding ? 'جاري الإرسال...' : `إرسال تذكير لـ ${issuedCount - boardedCount} مقعد لم يصل صاحبه`}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="py-16 text-center text-gray-400">جاري التحميل...</div>
          ) : bookings.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <p className="text-3xl mb-2">🪑</p><p>لا يوجد مسافرون بعد</p>
            </div>
          ) : (
            <table className="w-full text-sm text-right">
              <thead className="bg-gray-50 text-gray-500 sticky top-0">
                <tr>
                  <th className="px-4 py-2.5 font-medium">#</th>
                  <th className="px-4 py-2.5 font-medium">المسافر</th>
                  <th className="px-4 py-2.5 font-medium">الهاتف</th>
                  <th className="px-4 py-2.5 font-medium">رقم التذكرة</th>
                  <th className="px-4 py-2.5 font-medium">المقعد</th>
                  <th className="px-4 py-2.5 font-medium">المبلغ</th>
                  <th className="px-4 py-2.5 font-medium">الصعود</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {allRows.map((row, i) => {
                  const b = row.booking;
                  return (
                    <tr key={`${b.id}-${row.seat}`} className={`transition ${b.boardedAt ? 'bg-green-50/40' : 'hover:bg-gray-50'}`}>
                      <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{row.name}</td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs" dir="ltr">{row.phone}</td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {row.ticketNumber ? (
                          <span className="text-blue-700">{row.ticketNumber}</span>
                        ) : (
                          <span className="text-gray-400">{b.bookingNumber}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded font-medium">
                          {row.seat}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800">
                        {row.isFirst
                          ? `${parseFloat(b.totalAmount).toLocaleString()} ج`
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {b.boardedAt ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                            ✓ صعد {format(new Date(b.boardedAt), 'HH:mm')}
                          </span>
                        ) : b.status === 'ticket_issued' ? (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">لم يصل</span>
                        ) : (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                            {BOOKING_STATUS_LABELS[b.status]?.label || b.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t flex-shrink-0 flex gap-3">
          <button onClick={load} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-xl text-sm font-medium transition">
            تحديث
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-xl text-sm font-medium transition">
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
};

const STATUS_COLORS = {
  scheduled:   'bg-blue-100 text-blue-700',
  boarding:    'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-orange-100 text-orange-700',
  completed:   'bg-green-100 text-green-700',
  cancelled:   'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  scheduled: 'مجدولة', boarding: 'الصعود', in_progress: 'في الطريق',
  completed: 'منتهية', cancelled: 'ملغاة',
};

// ── Edit modal ────────────────────────────────────────────────────────────────
const EditModal = ({ trip, buses, promos, onClose, onSaved }) => {
  const { register, handleSubmit, formState: { isSubmitting } } = useForm({
    defaultValues: {
      fromCity:      trip.fromCity,
      toCity:        trip.toCity,
      departureTime: trip.departureTime ? fmtUTCInput(trip.departureTime) : '',
      arrivalTime:   trip.arrivalTime   ? fmtUTCInput(trip.arrivalTime)   : '',
      price:         trip.price,
      notes:         trip.notes || '',
      tripType:      trip.tripType || '',
      remarks:       trip.remarks || '',
      promoId:       trip.promoId || '',
      parcelServiceEnabled: trip.parcelServiceEnabled !== false,
      maxParcelWeightKg:    trip.maxParcelWeightKg ?? '',
      maxParcelCount:       trip.maxParcelCount ?? '',
    },
  });

  const onSubmit = async (data) => {
    try {
      data.price = parseFloat(data.price);
      const res = await tripAPI.update(trip.id, data);
      toast.success(res.data.message || 'تم تحديث الرحلة');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || 'فشل في تحديث الرحلة');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">✏️ تعديل الرحلة</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">مدينة الانطلاق</label>
              <input {...register('fromCity', { required: true })} className="input-field" list="safer-city-names" />
            </div>
            <div>
              <label className="label">مدينة الوصول</label>
              <input {...register('toCity', { required: true })} className="input-field" list="safer-city-names" />
            </div>
            <div>
              <label className="label">موعد الانطلاق</label>
              <input type="datetime-local" {...register('departureTime', { required: true })} className="input-field" />
            </div>
            <div>
              <label className="label">موعد الوصول</label>
              <input type="datetime-local" {...register('arrivalTime', { required: true })} className="input-field" />
            </div>
            <div>
              <label className="label">السعر (جنيه)</label>
              <input type="number" step="0.01" {...register('price', { required: true })} className="input-field" />
            </div>
            <div>
              <label className="label">تصنيف الرحلة</label>
              <select {...register('tripType')} className="input-field">
                <option value="">— غير محدد —</option>
                <option value="short">🟢 قصيرة</option>
                <option value="medium">🟡 متوسطة</option>
                <option value="long">🔴 طويلة</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="label">ملاحظات التذكرة — نقطة التجمع وموعد الحضور</label>
              <input {...register('remarks')} className="input-field"
                placeholder="مثال: موقف الخرطوم البحري — بجوار بنك الخرطوم — الانطلاق الساعة 09:00" />
            </div>
            <div className="col-span-2">
              <label className="label">ملاحظات داخلية</label>
              <input {...register('notes')} className="input-field" placeholder="أي ملاحظات..." />
            </div>
            {promos.length > 0 && (
              <div className="col-span-2">
                <label className="label">🏷️ عرض الخصم (اختياري)</label>
                <select {...register('promoId')} className="input-field">
                  <option value="">— بدون خصم —</option>
                  {promos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} — {p.title} ({p.discountPercent}%)
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="border border-gray-200 rounded-xl p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <input type="checkbox" {...register('parcelServiceEnabled')} className="w-4 h-4" />
              📦 هذه الرحلة تقبل شحن الطرود
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">أقصى وزن طرود (كجم) <span className="text-gray-400 font-normal">اختياري</span></label>
                <input
                  type="text" inputMode="decimal" className="input-field"
                  placeholder="بدون حد — يتبع نوع المركبة"
                  {...register('maxParcelWeightKg', {
                    setValueAs: (v) => normalizeDecimalInput(v),
                    onChange: (e) => { e.target.value = normalizeDecimalInput(e.target.value); },
                  })}
                />
              </div>
              <div>
                <label className="label">أقصى عدد طرود <span className="text-gray-400 font-normal">اختياري</span></label>
                <input
                  type="text" inputMode="numeric" className="input-field" placeholder="بدون حد"
                  {...register('maxParcelCount', {
                    setValueAs: (v) => normalizeIntInput(v),
                    onChange: (e) => { e.target.value = normalizeIntInput(e.target.value); },
                  })}
                />
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-800">
            ⚠️ سيتم إرسال إشعار واتساب لجميع المسافرين المحجوزين عند تغيير أي بيانات.
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={isSubmitting}
              className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white py-2.5 rounded-xl font-semibold transition">
              {isSubmitting ? 'جاري الحفظ...' : 'حفظ التعديلات'}
            </button>
            <button type="button" onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl transition">
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Blocked seats modal ───────────────────────────────────────────────────────
// Top-view seat button for admin (blocked/booked/available states)
const AdminSeatBtn = ({ n, isBooked, isBlocked, onToggle }) => (
  <button
    onClick={() => !isBooked && onToggle(n)}
    disabled={isBooked}
    title={isBooked ? `مقعد ${n} — محجوز من مسافر` : isBlocked ? `مقعد ${n} — موقوف (انقر للإلغاء)` : `مقعد ${n} — متاح (انقر للإيقاف)`}
    className={`w-9 h-10 flex flex-col overflow-hidden rounded-md text-[10px] font-bold transition-all shrink-0
      ${isBooked ? 'cursor-not-allowed opacity-75' : 'cursor-pointer hover:brightness-110 active:scale-95'}`}
  >
    {/* Headrest */}
    <div className={`w-full shrink-0 rounded-t-md h-[35%] ${
      isBooked ? 'bg-blue-800' : isBlocked ? 'bg-red-700' : 'bg-gray-400'
    }`} />
    {/* Cushion + label */}
    <div className={`flex-1 w-full flex items-center justify-center font-bold ${
      isBooked
        ? 'bg-blue-500/80 text-white'
        : isBlocked
        ? 'bg-red-500 text-white'
        : 'bg-gray-200 text-gray-600 hover:bg-orange-100'
    }`}>
      {isBooked ? '✓' : isBlocked ? '🔒' : n}
    </div>
  </button>
);

const BlockedSeatsModal = ({ trip, onClose, onSaved }) => {
  const totalSeats = trip.bus?.totalSeats || 43;
  const { cfg, seatsPerRow: perRow, rows, backSeats } = generateVehicleLayout(totalSeats, trip.bus);
  const booked     = trip.bookedSeats || [];
  const [blocked, setBlocked] = useState([...(trip.blockedSeats || [])]);
  const [saving, setSaving]   = useState(false);

  const toggle = (n) => {
    setBlocked((prev) =>
      prev.includes(n) ? prev.filter((s) => s !== n) : [...prev, n]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await tripAPI.updateBlockedSeats(trip.id, blocked);
      toast.success('تم حفظ المقاعد الموقوفة');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || 'فشل في الحفظ');
    } finally { setSaving(false); }
  };

  // Horizontal layout helpers (same pattern as passenger page)
  const mainRows2 = rows.map(r => r.flat);
  const backRow2  = backSeats.length > 0 ? backSeats : null;
  const columns2  = [...mainRows2].reverse();
  const doorAfterCol2 = Math.floor(columns2.length * 0.5);

  const AdminDoorDivider = () => (
    <div className="self-stretch flex flex-col items-center justify-center mx-1 shrink-0">
      <div className="h-full w-0 border-r-2 border-dashed border-amber-400/70 relative">
        <span className="absolute top-1/2 -translate-y-1/2 -right-2.5 text-[10px]">🚪</span>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" dir="rtl">
        <div className="p-5 border-b flex items-start justify-between gap-3 flex-shrink-0">
          <div>
            <h2 className="font-bold text-gray-800">🔒 إيقاف مقاعد</h2>
            <p className="text-xs text-gray-400 mt-0.5">{trip.fromCity} → {trip.toCity}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none flex-shrink-0">×</button>
        </div>

        {/* Legend */}
        <div className="px-5 pt-3 pb-2 flex-shrink-0 flex gap-5 text-xs flex-wrap">
          <span className="flex items-center gap-1.5">
            <span className="w-5 h-6 rounded-md bg-gray-200 border border-gray-300 inline-block" />
            متاح — انقر للإيقاف
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-5 h-6 rounded-md bg-red-500 inline-block" />
            موقوف — انقر للإلغاء
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-5 h-6 rounded-md bg-blue-500 inline-block" />
            محجوز من مسافر (لا يمكن تغييره)
          </span>
        </div>

        {/* Stats */}
        <div className="px-5 pb-3 flex-shrink-0 flex gap-4 text-xs text-gray-500">
          <span>الإجمالي: <strong className="text-gray-700">{totalSeats}</strong></span>
          <span>محجوز: <strong className="text-blue-600">{booked.length}</strong></span>
          <span>موقوف: <strong className="text-red-600">{blocked.length}</strong></span>
          <span>متاح: <strong className="text-green-600">{totalSeats - booked.length - blocked.length}</strong></span>
        </div>

        {/* Horizontal bus diagram */}
        <div className="overflow-x-auto flex-1 pb-3 px-4 select-none" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div dir="ltr" className="inline-block py-2">
            <div
              className="relative flex flex-row items-stretch bg-gray-950 shadow-xl border border-gray-800"
              style={{ borderRadius: '10px 36px 36px 10px' }}
            >
              {/* Back wall */}
              <div className="w-4 bg-gray-800 rounded-l-[10px] shrink-0 border-r border-gray-700" />

              {/* Back row */}
              {backRow2 && (
                <div className="flex flex-col items-center justify-center gap-0.5 bg-gray-800/50 px-1.5 border-r-2 border-dashed border-gray-600/70 py-2 shrink-0">
                  {backRow2.map(n => (
                    <AdminSeatBtn key={n} n={n} isBooked={booked.includes(n)} isBlocked={blocked.includes(n)} onToggle={toggle} />
                  ))}
                </div>
              )}

              {/* Main seating area */}
              <div className="flex flex-col">
                {/* Top window strip */}
                <div className="h-3 bg-sky-950 border-b border-sky-800/60 flex items-center gap-1 px-1 overflow-hidden shrink-0">
                  {columns2.map((_, i) => <div key={i} className="w-7 h-1.5 bg-sky-600/40 rounded-full shrink-0 border border-sky-500/30" />)}
                </div>

                {/* Top seats */}
                <div className="flex flex-row items-end gap-1 px-1.5 pt-1.5">
                  {columns2.map((row, ci) => (
                    <React.Fragment key={ci}>
                      <div className="flex flex-col gap-0.5">
                        {row.slice(0, cfg.left).map(n => (
                          <AdminSeatBtn key={n} n={n} isBooked={booked.includes(n)} isBlocked={blocked.includes(n)} onToggle={toggle} />
                        ))}
                      </div>
                      {ci === doorAfterCol2 && <AdminDoorDivider />}
                    </React.Fragment>
                  ))}
                </div>

                {/* Aisle */}
                <div className="mx-1.5 my-1 h-4 bg-gray-800/50 rounded flex items-center">
                  <div className="flex-1 border-t border-dashed border-gray-700/80" />
                  <span className="text-[8px] text-gray-600 px-2 shrink-0">ممر</span>
                  <div className="flex-1 border-t border-dashed border-gray-700/80" />
                </div>

                {/* Bottom seats */}
                <div className="flex flex-row items-start gap-1 px-1.5 pb-1.5">
                  {columns2.map((row, ci) => (
                    <React.Fragment key={ci}>
                      <div className="flex flex-col gap-0.5">
                        {row.slice(cfg.left).map(n => (
                          <AdminSeatBtn key={n} n={n} isBooked={booked.includes(n)} isBlocked={blocked.includes(n)} onToggle={toggle} />
                        ))}
                      </div>
                      {ci === doorAfterCol2 && <AdminDoorDivider />}
                    </React.Fragment>
                  ))}
                </div>

                {/* Bottom window strip */}
                <div className="h-3 bg-sky-950 border-t border-sky-800/60 flex items-center gap-1 px-1 overflow-hidden shrink-0">
                  {columns2.map((_, i) => <div key={i} className="w-7 h-1.5 bg-sky-600/40 rounded-full shrink-0 border border-sky-500/30" />)}
                </div>
              </div>

              {/* Front / driver */}
              <div className="w-16 bg-gray-900 flex flex-col items-center justify-center gap-2 border-l border-gray-800 shrink-0 rounded-r-[36px]">
                <div className="w-10 h-10 rounded-full border-[2.5px] border-gray-500 bg-gray-800 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#9ca3af" strokeWidth="2.2" strokeLinecap="round">
                    <circle cx="12" cy="12" r="8.5"/>
                    <circle cx="12" cy="12" r="2.2" fill="#9ca3af"/>
                    <line x1="12" y1="3.5" x2="12" y2="9.8"/>
                    <line x1="3.5" y1="15.5" x2="9.5" y2="12.2"/>
                    <line x1="20.5" y1="15.5" x2="14.5" y2="12.2"/>
                  </svg>
                </div>
                <span className="text-[8px] text-gray-500 font-medium">سائق</span>
              </div>
            </div>

            {/* Direction labels */}
            <div className="flex justify-between mt-1.5 px-5 text-[10px] text-gray-400">
              <span>← مؤخرة الباص</span>
              <span>مقدمة الباص →</span>
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex-shrink-0 flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white py-2.5 rounded-xl font-semibold text-sm transition"
          >
            {saving ? 'جاري الحفظ...' : 'حفظ المقاعد الموقوفة'}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl text-sm">
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Confirm modal (shared for suspend & cancel) ───────────────────────────────
const ConfirmModal = ({ title, message, confirmLabel, confirmClass, onConfirm, onClose, loading }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" dir="rtl">
      <div className="p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-2">{title}</h2>
        <p className="text-gray-500 text-sm mb-5">{message}</p>
        <div className="flex gap-3">
          <button onClick={onConfirm} disabled={loading}
            className={`flex-1 ${confirmClass} text-white py-2.5 rounded-xl font-semibold transition disabled:opacity-60`}>
            {loading ? 'جاري التنفيذ...' : confirmLabel}
          </button>
          <button onClick={onClose} disabled={loading}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl transition">
            تراجع
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const TripsPage = () => {
  const { user } = useAuth();
  const [trips, setTrips]             = useState([]);
  const [buses, setBuses]             = useState([]);
  const [showForm, setShowForm]       = useState(false);
  const [editTrip, setEditTrip]       = useState(null);
  const [modal, setModal]             = useState(null);
  const [passengerTrip, setPassengerTrip] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [seatsModal, setSeatsModal]       = useState(null);
  const [blockedModal, setBlockedModal]   = useState(null);
  const [cityCodes, setCityCodes]         = useState({});
  const [cityNames, setCityNames]         = useState([]);
  const [promos, setPromos]               = useState([]);

  const { register, handleSubmit, reset } = useForm({ defaultValues: { parcelServiceEnabled: true } });

  const fetchData = () => {
    Promise.all([tripAPI.getCompanyTrips(), busAPI.getAll()])
      .then(([tripsRes, busesRes]) => {
        setTrips(tripsRes.data.data);
        setBuses(busesRes.data.data.filter((b) => b.isActive));
      })
      .catch(() => toast.error('فشل في تحميل البيانات'));
  };

  useEffect(() => {
    fetchData();
    cityCodeAPI.getAll()
      .then((res) => {
        const rows = res.data.data || [];
        setCityCodes(Object.fromEntries(rows.map((r) => [r.name, r.code])));
        setCityNames(rows.map((r) => r.name));
      })
      .catch(() => {});
    promoAPI.getMine()
      .then((res) => setPromos((res.data.data || []).filter((p) => p.isEnabled && p.discountPercent > 0)))
      .catch(() => {});
  }, []);

  const onSubmit = async (data) => {
    try {
      if (data.costs) {
        data.costs = { fuel: parseFloat(data.fuel || 0), maintenance: parseFloat(data.maintenance || 0), crew: parseFloat(data.crew || 0), other: 0 };
      }
      data.price = parseFloat(data.price);
      await tripAPI.create(data);
      toast.success('تم إنشاء الرحلة بنجاح');
      reset();
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في إنشاء الرحلة');
    }
  };

  const handleSuspend = async () => {
    setActionLoading(true);
    try {
      const res = await tripAPI.suspend(modal.trip.id);
      toast.success(res.data.message);
      setModal(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || 'فشل في تعليق الرحلة');
    } finally { setActionLoading(false); }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      const res = await tripAPI.cancel(modal.trip.id);
      toast.success(res.data.message);
      setModal(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || 'فشل في إلغاء الرحلة');
    } finally { setActionLoading(false); }
  };

  const handleUpdateSeats = async () => {
    const { trip, value } = seatsModal;
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return toast.error('أدخل قيمة صحيحة');
    setSeatsModal((s) => ({ ...s, saving: true }));
    try {
      await tripAPI.setAvailableSeats(trip.id, num);
      toast.success('تم تحديث عدد المقاعد المتاحة');
      setSeatsModal(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || 'فشل في التحديث');
      setSeatsModal((s) => ({ ...s, saving: false }));
    }
  };

  const canEdit    = (trip) => trip.status === 'scheduled';
  const canSuspend = (trip) => ['scheduled'].includes(trip.status);
  const canCancel  = (trip) => !['cancelled', 'completed'].includes(trip.status);

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-6">
      {/* City name suggestions — sourced from the super-admin's registered city list;
          typing an unlisted city is still allowed since <input list> isn't constrained to it */}
      <datalist id="safer-city-names">
        {cityNames.map((name) => <option key={name} value={name} />)}
      </datalist>

      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">إدارة الرحلات</h1>
        <button onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 md:px-5 py-2 rounded-lg text-sm md:text-base whitespace-nowrap">
          {showForm ? 'إلغاء' : '+ رحلة جديدة'}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold mb-4">جدولة رحلة جديدة</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">مدينة الانطلاق *</label>
              <input {...register('fromCity', { required: true })} className="input-field" placeholder="الخرطوم" list="safer-city-names" />
            </div>
            <div>
              <label className="label">مدينة الوصول *</label>
              <input {...register('toCity', { required: true })} className="input-field" placeholder="بورتسودان" list="safer-city-names" />
            </div>
            <div>
              <label className="label">موعد الانطلاق *</label>
              <input type="datetime-local" {...register('departureTime', { required: true })} className="input-field" />
            </div>
            <div>
              <label className="label">موعد الوصول المتوقع *</label>
              <input type="datetime-local" {...register('arrivalTime', { required: true })} className="input-field" />
            </div>
            <div>
              <label className="label">السعر (جنيه) *</label>
              <input type="number" step="0.01" {...register('price', { required: true })} className="input-field" placeholder="150" />
            </div>
            <div>
              <label className="label">الباص *</label>
              <select {...register('busId', { required: true })} className="input-field">
                <option value="">اختر الباص...</option>
                {buses.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.vehicleType ? `${b.vehicleType.icon} ${b.vehicleType.name} — ` : ''}
                    {b.manufacturer} {b.model} - {b.plateNumber} ({b.totalSeats} مقعد)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">تكلفة الوقود (جنيه)</label>
              <input type="number" {...register('fuel')} className="input-field" placeholder="0" />
            </div>
            <div>
              <label className="label">تكلفة الصيانة (جنيه)</label>
              <input type="number" {...register('maintenance')} className="input-field" placeholder="0" />
            </div>
            <div>
              <label className="label">بدلات الطاقم (جنيه)</label>
              <input type="number" {...register('crew')} className="input-field" placeholder="0" />
            </div>
            <div>
              <label className="label">تصنيف الرحلة</label>
              <select {...register('tripType')} className="input-field">
                <option value="">— غير محدد —</option>
                <option value="short">🟢 قصيرة</option>
                <option value="medium">🟡 متوسطة</option>
                <option value="long">🔴 طويلة</option>
              </select>
            </div>
            <div>
              <label className="label">ملاحظات داخلية</label>
              <input {...register('notes')} className="input-field" placeholder="أي ملاحظات إضافية..." />
            </div>
            <div className="md:col-span-2">
              <label className="label">ملاحظات التذكرة — نقطة التجمع وموعد الحضور</label>
              <input {...register('remarks')} className="input-field"
                placeholder="مثال: موقف الخرطوم البحري — بجوار بنك الخرطوم — الانطلاق الساعة 09:00" />
            </div>
            {promos.length > 0 && (
              <div className="md:col-span-2">
                <label className="label">🏷️ عرض الخصم (اختياري)</label>
                <select {...register('promoId')} className="input-field">
                  <option value="">— بدون خصم —</option>
                  {promos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} — {p.title} ({p.discountPercent}%)
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="md:col-span-2 border border-gray-200 rounded-xl p-4 space-y-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <input type="checkbox" {...register('parcelServiceEnabled')} className="w-4 h-4" />
                📦 هذه الرحلة تقبل شحن الطرود
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">أقصى وزن طرود (كجم) <span className="text-gray-400 font-normal">اختياري</span></label>
                  <input
                  type="text" inputMode="decimal" className="input-field"
                  placeholder="بدون حد — يتبع نوع المركبة"
                  {...register('maxParcelWeightKg', {
                    setValueAs: (v) => normalizeDecimalInput(v),
                    onChange: (e) => { e.target.value = normalizeDecimalInput(e.target.value); },
                  })}
                />
                </div>
                <div>
                  <label className="label">أقصى عدد طرود <span className="text-gray-400 font-normal">اختياري</span></label>
                  <input
                  type="text" inputMode="numeric" className="input-field" placeholder="بدون حد"
                  {...register('maxParcelCount', {
                    setValueAs: (v) => normalizeIntInput(v),
                    onChange: (e) => { e.target.value = normalizeIntInput(e.target.value); },
                  })}
                />
                </div>
              </div>
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button type="submit" className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg">
                إنشاء الرحلة
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Trips table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-right min-w-[700px]">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3">المسار</th>
                <th className="px-4 py-3">الانطلاق</th>
                <th className="px-4 py-3">الباص</th>
                <th className="px-4 py-3">الإشغال</th>
                <th className="px-4 py-3">السعر</th>
                <th className="px-4 py-3">الحالة</th>
                <th className="px-4 py-3 text-center">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {trips.map((trip) => (
                <tr key={trip.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3 font-medium">
                    <button
                      onClick={() => setPassengerTrip(trip)}
                      className="text-blue-700 hover:text-blue-900 hover:underline text-right font-medium transition"
                      title="عرض قائمة المسافرين"
                    >
                      {trip.fromCity} → {trip.toCity}
                    </button>
                    {trip.isSuspended && (
                      <span className="mr-2 text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-semibold">معلقة</span>
                    )}
                    {trip.tripType && (
                      <span className={`mr-1 text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                        trip.tripType === 'short'  ? 'bg-green-100 text-green-700' :
                        trip.tripType === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {trip.tripType === 'short' ? 'قصيرة' : trip.tripType === 'medium' ? 'متوسطة' : 'طويلة'}
                      </span>
                    )}
                    {trip.promo && (
                      <span className="mr-1 text-[10px] px-1.5 py-0.5 rounded font-semibold bg-amber-100 text-amber-700">
                        🏷️ {trip.promo.code} {trip.promo.discountPercent}%
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {fmtUTC(trip.departureTime)}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {trip.bus?.model} ({trip.bus?.plateNumber})
                  </td>
                  <td className="px-4 py-3">
                    {(trip.bookedSeats?.length || 0)} / {(trip.bookedSeats?.length || 0) + (trip.availableSeats || 0)}
                  </td>
                  <td className="px-4 py-3 text-blue-600 font-medium">{trip.price} ج</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[trip.status]}`}>
                      {STATUS_LABELS[trip.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      {/* Edit */}
                      {canEdit(trip) && (
                        <button
                          onClick={() => setEditTrip(trip)}
                          className="flex items-center gap-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2.5 py-1.5 rounded-lg transition font-medium"
                          title="تعديل"
                        >
                          ✏️ تعديل
                        </button>
                      )}

                      {/* Available seats */}
                      {!['cancelled', 'completed'].includes(trip.status) && (
                        <button
                          onClick={() => setSeatsModal({ trip, value: String(trip.availableSeats ?? 0), saving: false })}
                          className="flex items-center gap-1 text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 px-2.5 py-1.5 rounded-lg transition font-medium"
                          title="تعديل المقاعد المتاحة"
                        >
                          🎟 المتاحة
                        </button>
                      )}

                      {/* Blocked seats */}
                      {!['cancelled', 'completed'].includes(trip.status) && (
                        <button
                          onClick={() => setBlockedModal(trip)}
                          className="flex items-center gap-1 text-xs bg-red-50 hover:bg-red-100 text-red-700 px-2.5 py-1.5 rounded-lg transition font-medium"
                          title="إيقاف مقاعد"
                        >
                          🔒{(trip.blockedSeats?.length > 0) ? ` ${trip.blockedSeats.length}` : ''}
                        </button>
                      )}

                      {/* Suspend / Unsuspend */}
                      {canSuspend(trip) && (
                        <button
                          onClick={() => setModal({ type: trip.isSuspended ? 'unsuspend' : 'suspend', trip })}
                          className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg transition font-medium ${
                            trip.isSuspended
                              ? 'bg-green-50 hover:bg-green-100 text-green-700'
                              : 'bg-orange-50 hover:bg-orange-100 text-orange-700'
                          }`}
                          title={trip.isSuspended ? 'استئناف' : 'تعليق'}
                        >
                          {trip.isSuspended ? '▶️ استئناف' : '⏸ تعليق'}
                        </button>
                      )}

                      {/* Cancel */}
                      {canCancel(trip) && (
                        <button
                          onClick={() => setModal({ type: 'cancel', trip })}
                          className="flex items-center gap-1 text-xs bg-red-50 hover:bg-red-100 text-red-700 px-2.5 py-1.5 rounded-lg transition font-medium"
                          title="إلغاء الرحلة"
                        >
                          ❌ إلغاء
                        </button>
                      )}

                      {!canEdit(trip) && !canSuspend(trip) && !canCancel(trip) && (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {trips.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-gray-400 py-12">
                    <div className="text-3xl mb-2">🚌</div>
                    لا توجد رحلات
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit modal */}
      {editTrip && (
        <EditModal
          trip={editTrip}
          buses={buses}
          promos={promos}
          onClose={() => setEditTrip(null)}
          onSaved={() => { setEditTrip(null); fetchData(); }}
        />
      )}

      {/* Suspend confirm */}
      {modal?.type === 'suspend' && (
        <ConfirmModal
          title="⏸ تعليق الرحلة"
          message={`هل تريد تعليق رحلة ${modal.trip.fromCity} → ${modal.trip.toCity}؟ لن يتمكن المسافرون من إتمام حجوزات جديدة، وسيتم إشعار المحجوزين الحاليين.`}
          confirmLabel="نعم، تعليق الرحلة"
          confirmClass="bg-orange-600 hover:bg-orange-700"
          onConfirm={handleSuspend}
          onClose={() => setModal(null)}
          loading={actionLoading}
        />
      )}

      {/* Unsuspend confirm */}
      {modal?.type === 'unsuspend' && (
        <ConfirmModal
          title="▶️ استئناف الرحلة"
          message={`هل تريد استئناف رحلة ${modal.trip.fromCity} → ${modal.trip.toCity}؟ ستعود الرحلة للظهور في نتائج البحث وسيتم إشعار المسافرين.`}
          confirmLabel="نعم، استئناف الرحلة"
          confirmClass="bg-green-600 hover:bg-green-700"
          onConfirm={handleSuspend}
          onClose={() => setModal(null)}
          loading={actionLoading}
        />
      )}

      {/* Cancel confirm */}
      {modal?.type === 'cancel' && (
        <ConfirmModal
          title="❌ إلغاء الرحلة"
          message={`هل أنت متأكد من إلغاء رحلة ${modal.trip.fromCity} → ${modal.trip.toCity} بتاريخ ${fmtUTCDate(modal.trip.departureTime)}؟ سيتم إلغاء جميع الحجوزات النشطة وإشعار المسافرين عبر واتساب. هذا الإجراء لا يمكن التراجع عنه.`}
          confirmLabel="نعم، إلغاء الرحلة نهائياً"
          confirmClass="bg-red-600 hover:bg-red-700"
          onConfirm={handleCancel}
          onClose={() => setModal(null)}
          loading={actionLoading}
        />
      )}

      {/* Passenger manifest modal */}
      {passengerTrip && (
        <PassengerModal
          trip={passengerTrip}
          onClose={() => setPassengerTrip(null)}
          codes={cityCodes}
        />
      )}

      {/* Blocked seats modal */}
      {blockedModal && (
        <BlockedSeatsModal
          trip={blockedModal}
          onClose={() => setBlockedModal(null)}
          onSaved={() => { setBlockedModal(null); fetchData(); }}
        />
      )}

      {/* Available seats modal */}
      {seatsModal && (() => {
        const { trip, value, saving } = seatsModal;
        const booked     = trip.bookedSeats?.length || 0;
        const totalSeats = trip.bus?.totalSeats || 0;
        const maxAllowed = totalSeats - booked;
        const numVal     = parseInt(value, 10);
        const isValid    = !isNaN(numVal) && numVal >= 0 && numVal <= maxAllowed;
        return (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" dir="rtl">
              <div className="p-5 border-b flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-gray-800">🎟 تعديل المقاعد المتاحة</h2>
                  <p className="text-xs text-gray-400 mt-0.5">{trip.fromCity} → {trip.toCity}</p>
                </div>
                <button onClick={() => setSeatsModal(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
              </div>
              <div className="p-5 space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 text-center text-sm">
                  <div className="bg-gray-50 rounded-xl py-3">
                    <p className="text-2xl font-bold text-gray-800">{totalSeats}</p>
                    <p className="text-xs text-gray-500 mt-0.5">إجمالي المقاعد</p>
                  </div>
                  <div className="bg-red-50 rounded-xl py-3">
                    <p className="text-2xl font-bold text-red-600">{booked}</p>
                    <p className="text-xs text-red-400 mt-0.5">محجوزة</p>
                  </div>
                  <div className="bg-green-50 rounded-xl py-3">
                    <p className="text-2xl font-bold text-green-600">{trip.availableSeats}</p>
                    <p className="text-xs text-green-500 mt-0.5">متاحة حالياً</p>
                  </div>
                </div>

                {/* Input */}
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-1.5">
                    عدد المقاعد المتاحة للحجز
                    <span className="mr-1 text-gray-400 font-normal">(الحد الأقصى: {maxAllowed})</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={maxAllowed}
                    value={value}
                    onChange={(e) => setSeatsModal((s) => ({ ...s, value: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-lg font-bold text-center focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  {!isNaN(numVal) && numVal < trip.availableSeats && (
                    <p className="text-xs text-orange-600 mt-1">
                      سيتم حجز {trip.availableSeats - numVal} مقعد داخلياً وإخفاؤها من البحث
                    </p>
                  )}
                  {!isNaN(numVal) && numVal > trip.availableSeats && (
                    <p className="text-xs text-green-600 mt-1">
                      سيتم إتاحة {numVal - trip.availableSeats} مقعد إضافي للحجز
                    </p>
                  )}
                </div>
              </div>
              <div className="p-5 border-t flex gap-3">
                <button
                  onClick={handleUpdateSeats}
                  disabled={saving || !isValid}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2.5 rounded-xl font-semibold text-sm transition"
                >
                  {saving ? 'جاري الحفظ...' : 'حفظ'}
                </button>
                <button
                  onClick={() => setSeatsModal(null)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl text-sm"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default TripsPage;
